import style from './TradePlanHubListItem.module.css';
import { TradePlan } from '../../../../../classes/tradePlan/tradePlanTypes';
import { Body1, Body2, Body3, fontSizes } from '../../../../atomic/text/Text';
import { BiDirectionalArrow } from '../../../../atomic/atomicComponentLibrary/icons/biDirectionalArrow/BiDirectionalArrow';
import { UpArrow } from '../../../../atomic/atomicComponentLibrary/icons/upArrow/UpArrow';
import { DownArrow } from '../../../../atomic/atomicComponentLibrary/icons/downArrow/DownArrow';
import { ColorLetterBox } from '../../../../atomic/atomicComponentLibrary/icons/colorLetterBox/ColorLetterBox';
import { colors } from '../../../../../colorVars';
import { useWebsocketStore } from '../../../../../stores/websocketStore/websocketProvider';
import { BidAskComponent } from './components/bidAsk/BidAskComponent';
import { PnLComponent } from './components/pnl/PnLComponent';
import { PositionComponent } from './components/position/PositionComponent';
import '../../../../../colorVars.css';
import { getStrikeAndType } from './utils/getStrikeAndType';
import { SetupComponent } from './components/setup/SetupComponent';
import { getSetupName } from './components/setup/getSetupName';
import { PauseButton } from '../../../../atomic/buttons/pauseButtons/PauseButton';
import {
  mapAccountsFromBalances,
  resolveMarketDataBrokerage,
} from '../../../../../utils/accountRouting';

const getFlattenColor = (attemptCount: number): string => {
  // count=1: amber; interpolates to deep red by count=6+
  const t = Math.min((attemptCount - 1) / 5, 1);
  const r = 255;
  const g = Math.round(160 * (1 - t));
  const b = Math.round(80 * (1 - t) * (1 - Math.min(t * 2, 1)));
  return `rgb(${r}, ${g}, ${b})`;
};

type Props = {
  tradePlanId: string;
  selectedTradePlan: string;
  setSelectedTradePlan: (tradePlanId: string) => void;
  previous: string;
  lastItem: boolean;
};

export const TradePlanHubListItem = ({
  tradePlanId,
  selectedTradePlan,
  setSelectedTradePlan,
  previous,
  lastItem,
}: Props) => {
  const { tradePlanStore, accountBalanceStore } = useWebsocketStore();
  const tradePlan: TradePlan = tradePlanStore?.tradePlans[tradePlanId] ?? null;
  if (!tradePlan) return null;

  const accounts = mapAccountsFromBalances(accountBalanceStore?.balances);
  const template = tradePlan.savedConfigurationTitle
    ? tradePlan.savedConfigurationTitle.length > 23
      ? tradePlan.savedConfigurationTitle.slice(0, 23) + '...'
      : tradePlan.savedConfigurationTitle
    : getSetupName(tradePlan.setup);
  const inPosition = tradePlan.inPosition;
  const activeSymbol =
    inPosition &&
    tradePlan.positionSymbol?.startsWith('/') &&
    tradePlan.positionSymbol !== tradePlan.symbol
      ? tradePlan.positionSymbol
      : tradePlan.symbol;
  const direction =
    tradePlan.goLong && tradePlan.goShort
      ? 'both'
      : tradePlan.goLong
      ? 'long'
      : 'short';
  const marketDataBrokerage = resolveMarketDataBrokerage({
    accountNumber: tradePlan.accountNumber,
    accounts,
    currentBrokerage: tradePlan.brokerage,
    dataSource: tradePlan.dataSource,
    symbol: activeSymbol,
  });
  return (
    <li
      onClick={() => setSelectedTradePlan(tradePlanId)}
      className={
        selectedTradePlan === tradePlanId
          ? style.selectedListItem
          : selectedTradePlan === previous && !lastItem
          ? style.afterSelected
          : style.listItem
      }
    >
      <div
        className={style.innerWrapper}
        style={{
          background: `linear-gradient(90deg, ${
            tradePlan.restartSettings.deactivated
              ? 'rgba(125, 29, 42, 0.23)'
              : tradePlan.inPosition
              ? 'rgba(1, 74, 47, 0.23)'
              : 'rgba(92, 1, 92, 0.23)'
          }, ${colors.grey})`,
        }}
      >
        <div className={style.letterWrapper}>
          {tradePlan.restartSettings.deactivated ? (
            <ColorLetterBox
              selected={selectedTradePlan === tradePlanId}
              backgroundColor="#7d1d2a"
              letter="D"
              width="30px"
              height="100%"
              fontSize={fontSizes.body1}
              margin="0"
            />
          ) : tradePlan.inPosition ? (
            <ColorLetterBox
              selected={selectedTradePlan === tradePlanId}
              backgroundColor="#014a2f"
              letter="E"
              width="30px"
              height="100%"
              fontSize={fontSizes.body1}
              margin="0"
            />
          ) : (
            <ColorLetterBox
              selected={selectedTradePlan === tradePlanId}
              backgroundColor="#5c015c"
              letter="A"
              width="30px"
              height="100%"
              fontSize={fontSizes.body1}
              margin="0"
            />
          )}
        </div>
        <div className={style.rightHalf}>
          <div className={style.topHalf}>
            <div className={style.directionWapper}>
              {direction === 'both' ? (
                <BiDirectionalArrow size="17px" margin="0" />
              ) : direction === 'long' ? (
                <UpArrow size="17px" margin="0" />
              ) : (
                <DownArrow size="17px" margin="0" />
              )}
            </div>
            <div>
              <div>
                <Body1 color={colors.lightGrey} fontWeight={600}>
                  {activeSymbol}
                </Body1>
                {tradePlan.positionSymbol &&
                tradePlan.positionSymbol?.length > 6 ? (
                  <Body3 color={colors.lightGrey} fontWeight={500}>
                    {' '}
                    [{getStrikeAndType(tradePlan.positionSymbol)}]
                  </Body3>
                ) : null}
              </div>
            </div>
            <div className={style.templateNameCell}>
              <Body2 italic={true} color={colors.lightGrey}>
                {template}
              </Body2>
              {tradePlan.flatteningIntent?.active && (tradePlan.flatteningIntent.attemptCount ?? 0) > 0 ? (
                <svg
                  className={style.flatteningIcon}
                  viewBox="0 0 24 24"
                  fill={getFlattenColor(tradePlan.flatteningIntent.attemptCount ?? 0)}
                >
                  <title>{`${tradePlan.flatteningIntent.reason ?? 'Flattening'} (attempt ${tradePlan.flatteningIntent.attemptCount ?? 0})`}</title>
                  <path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                </svg>
              ) : null}
            </div>
          </div>
          <div className={style.bottomHalf}>
            <div className={style.directionWapper}>
              {tradePlan.paused ? <PauseButton width={17} height={17} /> : null}
            </div>
            <BidAskComponent
              symbol={activeSymbol}
              dataSource={tradePlan.dataSource}
              brokerage={marketDataBrokerage}
            />
            {inPosition ? (
              <PnLComponent tradePlanId={tradePlanId} />
            ) : (
              <SetupComponent tradePlanId={tradePlanId} />
            )}
            {inPosition ? (
              <PositionComponent tradePlanId={tradePlanId} />
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
};

/* necessary info: 

  * symbol
  * template
  * direction
  * status
  * pnl
  * position size
  * fill price


*/
